/**
 * Workspace content (Q60): the one data input for everything seeded inside workspaces, shared by the bootstrap
 * (bootstrap/default-workspace-content.json) and the API tests (plus their add-only overlay,
 * lib/api-test/fixtures/content-overlay.json). Parsed at the caller's boundary; no fs here.
 *
 * Every item has a `key`, unique across the file (and across file + overlay). Items reference each other by key; the
 * seeder records each seeded item's fields under its key and resolves references through that map. Names and client-
 * chosen ids are not data: `naming.prefix` is prepended to every name-like field (participantId, group/tag/prompt/tool/
 * hook/handler names, storage filename) and `naming.idFor(key)` supplies client-chosen ids (participant, group, thread,
 * conversation, message, client instance). The bootstrap's `idFor` reads `ids` (fixed ids, so re-runs reach the same
 * rows); the tests mint per-run ids.
 */

import { z } from "zod";
import { schemas as cloudProviderSchemas } from "./cloud-provider-schemas";
import { schemas as messageStructureSchemas } from "./message-structure-schemas";
import { TagType } from "./tags-schemas";

const Key = z.string().regex(/^[a-z0-9][a-z0-9-]*$/);
const ScopeRef = z.string().regex(/^[A-Z][A-Z0-9_]*$/);
/** `<item key>.<field>`: one field of a seeded item's result. */
const FieldRef = z.string().regex(/^[a-z0-9][a-z0-9-]*\.[a-zA-Z]+$/);
const Name = z.string().min(1);
const MessageRole = messageStructureSchemas.MessageRole;

const ParticipantType = z.enum(["user", "bot"]);
const ParticipantStatus = z.enum(["online", "offline", "away", "busy", "typing"]);
const ParticipantRole = z.enum(["admin", "member", "guest", "moderator", "viewer"]);

const Participant = z.strictObject({
  key: Key,
  /** Public participantId (prefixed by naming); distinct from the row id (idFor(key)). */
  participantId: Name,
  name: Name,
  type: ParticipantType,
  avatar: z.string().nullable(),
  status: ParticipantStatus,
  role: ParticipantRole.nullable(),
});
const Group = z.strictObject({ key: Key, name: Name, description: z.string().nullable(), color: z.string().nullable() });
const GroupMember = z.strictObject({ key: Key, participant: Key, group: Key });
const Tag = z.strictObject({
  key: Key, name: Name, type: TagType, color: z.string().nullable(), description: z.string().nullable(), namespace: z.string().nullable(),
});
/** A thread and its conversation (both ids from idFor: the thread's key and `conversation`). */
const Thread = z.strictObject({ key: Key, conversation: Key });
const ThreadParticipant = z.strictObject({ key: Key, thread: Key, participant: Key });
/** A message: its MDC is uploaded (documentId = the message id) before the message is posted. */
const Message = z.strictObject({ key: Key, thread: Key, participant: Key, role: MessageRole, content: z.string().min(1) });
const Reaction = z.strictObject({ key: Key, message: Key, participant: Key, emoji: z.string().min(1) });
const Read = z.strictObject({ key: Key, message: Key, participant: Key });
/** A reply: its own message (MDC, then post) is created first, then linked to `message` (a message or a reply key). */
const Reply = z.strictObject({ key: Key, message: Key, participant: Key, role: MessageRole, content: z.string().min(1) });
const TagAssignment = z.strictObject({
  key: Key,
  tag: Key,
  entityType: TagType.extract(["thread", "chat-message", "participant", "group"]),
  /** Thread key (thread), message key (chat-message), participant key (participant) or group key (group). */
  entity: Key,
  /** Participant key; the assignment records its participantId. */
  assignedBy: Key,
});
const Composition = z.strictObject({ key: Key, thread: Key, participant: Key, content: z.string().min(1) });
const Reader = z.strictObject({ key: Key, thread: Key, participant: Key });
/** A pending invitation on `connector`'s channel into `thread`'s conversation; expiresAt = seed time + ttlMs. */
const Invitation = z.strictObject({ key: Key, connector: Key, thread: Key, from: Key, ttlMs: z.int().positive() });
/** An address mapped to `connector`'s channel in the place's workspace (written with the place's account credential). */
const AddressMapping = z.strictObject({ key: Key, connector: Key, address: z.string().min(1) });
/**
 * An external thread on `connector`'s channel mapped to `thread` (create-and-accept): externalThreadId =
 * `<channelType>_<providerType><sep><address><sep><sender>`, externalParticipantId = externalThreadId + sep + sender.
 */
const ThreadMapping = z.strictObject({ key: Key, connector: Key, thread: Key, address: Key, sender: z.string().min(1), from: Key });
/** A prompt whose content (`markdownFile`, a document the boundary reads) is uploaded as MDC before the upsert. */
const Prompt = z.strictObject({ key: Key, promptKey: Name, markdownFile: z.string().min(1) });
/** An object uploaded through the storage flow (prepare -> PUT -> complete). */
/** An object through the storage flow; `scopeLevel` 'account' is written with the place's account credentials (R31). */
const StorageObject = z.strictObject({
  key: Key, scopeLevel: z.enum(["account", "workspace"]), bucketKey: Name, filename: Name, contentType: z.string().min(1), content: z.string(),
});
/** A participant -> user mapping; `user` is a role-matrix identity ref resolved by the place. */
const IdentifierLink = z.strictObject({ key: Key, participant: Key, user: ScopeRef, linkSource: z.enum(["manual"]) });
const ClientInstance = z.strictObject({ key: Key, host: z.string().min(1), port: z.int().min(1).max(65535) });
const CloudScope = z.enum(["workspace", "tenant"]);
/** The route's create request plus the item key and scope (the request schemas are refined, so they are intersected). */
const Tool = z.object({ key: Key, scope: CloudScope }).and(cloudProviderSchemas.CreateCloudProviderToolRequest);
const Hook = z.object({ key: Key, scope: CloudScope }).and(cloudProviderSchemas.CreateCloudProviderHookRequest);
/** A webhook HITL handler: handlerConfig {url: <place webhookOrigin><webhook.path>/<scope>/<name>, signingSecret}. */
const HitlHandler = z.strictObject({
  key: Key,
  scope: CloudScope,
  name: Name,
  handlerType: z.literal("webhook"),
  timeoutSeconds: z.int().positive(),
  webhook: z.strictObject({ path: z.string().startsWith("/"), signingSecret: z.string().min(1) }),
});

const DemoUsers = z.strictObject({
  user: z.strictObject({
    type: z.string().min(1), tag: z.string().min(1), emailDomain: z.string().min(1),
    isGlobalAdmin: z.boolean(), isForbidden: z.boolean(), isDeleted: z.boolean(),
  }),
  users: z.array(z.strictObject({ participant: Key, username: Name, accountRole: z.enum(["registered", "admin"]), workspaceRole: z.enum(["registered", "admin"]) })),
});

/** One workspace's content. Every list is required (empty = none). */
export const PlaceContent = z.strictObject({
  /** Role-matrix scope ref of the workspace (bootstrap/role-matrix.json `scopes`). */
  workspace: ScopeRef,
  /** Provision the place's organization's public workspace (POST /system/public-workspace) first. */
  publicWorkspace: z.boolean(),
  /**
   * Connector key -> hierarchy names of the place's organization (bootstrap/default-hierarchy.yaml); `connection` is the
   * place's connection of that pair, null when the place only uses the org-level channel (e.g. W3 invitations on O1's).
   * Result fields: channelId, providerId, channelType (`<channel type>_<provider type>`), connectionId (null without one).
   */
  connectors: z.record(Key, z.strictObject({ channel: Name, provider: Name, connection: Name.nullable() })),
  participants: z.array(Participant),
  groups: z.array(Group),
  groupMembers: z.array(GroupMember),
  tags: z.array(Tag),
  threads: z.array(Thread),
  threadParticipants: z.array(ThreadParticipant),
  messages: z.array(Message),
  reactions: z.array(Reaction),
  reads: z.array(Read),
  replies: z.array(Reply),
  tagAssignments: z.array(TagAssignment),
  compositions: z.array(Composition),
  readers: z.array(Reader),
  storageObjects: z.array(StorageObject),
  prompts: z.array(Prompt),
  invitations: z.array(Invitation),
  addressMappings: z.array(AddressMapping),
  threadMappings: z.array(ThreadMapping),
  identifierLinks: z.array(IdentifierLink),
  clientInstances: z.array(ClientInstance),
  tools: z.array(Tool),
  hooks: z.array(Hook),
  hitlHandlers: z.array(HitlHandler),
  /** Casdoor demo users linked to participants (seeded by seedDemoUsers with operator clients, not by seedWorkspaceContent). */
  demoUsers: DemoUsers.nullable(),
});

const ITEM_LISTS = [
  "participants", "groups", "groupMembers", "tags", "threads", "threadParticipants", "messages", "reactions", "reads",
  "replies", "tagAssignments", "compositions", "readers", "storageObjects", "prompts", "invitations", "addressMappings",
  "threadMappings", "identifierLinks", "clientInstances", "tools", "hooks", "hitlHandlers",
] as const satisfies readonly (keyof z.infer<typeof PlaceContent>)[];

/** Item keys of `file`, each with the list it is in. */
export const itemKeys = (file: Pick<z.infer<typeof WorkspaceContentShape>, "places">): Map<string, string> => {
  const keys = new Map<string, string>();
  for (const place of file.places) {
    for (const list of ITEM_LISTS) for (const item of place[list]) keys.set(item.key, list);
    for (const item of place.threads) keys.set(item.conversation, "conversations");
    for (const key of Object.keys(place.connectors)) keys.set(key, "connectors");
  }
  return keys;
};

const WorkspaceContentShape = z.strictObject({
  /** Message MDC upload parameters (logical buckets, MDC tag entity type). */
  messageContent: z.strictObject({ mdcBucket: Name, attachmentsBucket: Name, tagEntityType: Name }),
  /** Prompt MDC upload parameters and the prompt upsert's refType; the MDC participant is the account with `role`. */
  promptContent: z.strictObject({ refType: z.literal("mdc"), mdcBucket: Name, attachmentsBucket: Name, role: MessageRole, tagEntityType: Name }),
  /** Separator of connector external ids (thread mappings). */
  externalIdSeparator: z.string().min(1),
  /** The bootstrap's fixed client-chosen ids, per item key (naming.idFor); the tests mint their own. */
  ids: z.record(Key, z.uuid()),
  /** Spec fixture name (P1, THREAD, ...) -> `<item key>.<field>` of the seeded result. */
  fixtureRefs: z.record(ScopeRef, FieldRef),
  places: z.array(PlaceContent),
});

const issues = (file: z.infer<typeof WorkspaceContentShape>, ctx: z.RefinementCtx) => {
  const issue = (path: (string | number)[], message: string) => ctx.addIssue({ code: "custom", path, message });
  // Keys are unique across the whole file: the seeded results form one key -> fields map.
  const seen = new Set<string>();
  const all: string[] = [];
  file.places.forEach((place, p) => {
    if (seen.has(place.workspace)) issue(["places", p, "workspace"], `place "${place.workspace}" appears twice`);
    seen.add(place.workspace);
    for (const list of ITEM_LISTS) all.push(...place[list].map((i) => i.key));
    all.push(...place.threads.map((t) => t.conversation), ...Object.keys(place.connectors));
  });
  const dup = all.filter((k, i) => all.indexOf(k) !== i);
  if (dup.length > 0) issue(["places"], `duplicate item keys: ${[...new Set(dup)].join(", ")}`);
  const keys = itemKeys(file);
  for (const [name, ref] of Object.entries(file.fixtureRefs)) {
    if (!keys.has(ref.split(".")[0]!)) issue(["fixtureRefs", name], `fixtureRefs.${name}: no item "${ref.split(".")[0]}"`);
  }
  for (const key of Object.keys(file.ids)) if (!keys.has(key)) issue(["ids", key], `ids: no item "${key}"`);
  // References resolve within the item's own place (lib/seed seeds one place at a time).
  file.places.forEach((place, p) => {
    const own = itemKeys({ places: [place] });
    const must = (path: (string | number)[], key: string, list: string) => {
      if (own.get(key) !== list) issue(["places", p, ...path], `"${key}" is no ${list} item of place ${place.workspace}`);
    };
    const connector = (path: (string | number)[], key: string) => must(path, key, "connectors");
    place.groupMembers.forEach((m, i) => { must(["groupMembers", i], m.participant, "participants"); must(["groupMembers", i], m.group, "groups"); });
    place.threadParticipants.forEach((t, i) => { must(["threadParticipants", i], t.thread, "threads"); must(["threadParticipants", i], t.participant, "participants"); });
    for (const list of ["messages", "compositions", "readers"] as const) {
      place[list].forEach((m, i) => { must([list, i], m.thread, "threads"); must([list, i], m.participant, "participants"); });
    }
    for (const list of ["reactions", "reads"] as const) {
      place[list].forEach((r, i) => { must([list, i], r.message, "messages"); must([list, i], r.participant, "participants"); });
    }
    place.replies.forEach((r, i) => {
      const target = own.get(r.message);
      if (target !== "messages" && target !== "replies") {
        issue(["places", p, "replies", i], `"${r.message}" is no messages or replies item of place ${place.workspace}`);
      }
      must(["replies", i], r.participant, "participants");
    });
    place.tagAssignments.forEach((a, i) => {
      must(["tagAssignments", i], a.tag, "tags");
      must(["tagAssignments", i], a.assignedBy, "participants");
      const entityList = { thread: "threads", "chat-message": "messages", participant: "participants", group: "groups" }[a.entityType];
      must(["tagAssignments", i], a.entity, entityList);
    });
    place.invitations.forEach((v, i) => { connector(["invitations", i], v.connector); must(["invitations", i], v.thread, "threads"); must(["invitations", i], v.from, "participants"); });
    place.addressMappings.forEach((a, i) => connector(["addressMappings", i], a.connector));
    place.threadMappings.forEach((m, i) => {
      connector(["threadMappings", i], m.connector);
      must(["threadMappings", i], m.thread, "threads");
      must(["threadMappings", i], m.address, "addressMappings");
      must(["threadMappings", i], m.from, "participants");
    });
    place.identifierLinks.forEach((l, i) => must(["identifierLinks", i], l.participant, "participants"));
    place.demoUsers?.users.forEach((u, i) => must(["demoUsers", "users", i], u.participant, "participants"));
    // A participant links to at most one user: a second link is a 409 at seed time.
    const linked = [...place.identifierLinks.map((l) => l.participant), ...(place.demoUsers === null ? [] : place.demoUsers.users.map((u) => u.participant))];
    for (const key of new Set(linked.filter((k, i) => linked.indexOf(k) !== i))) {
      issue(["places", p], `participant "${key}" is linked more than once`);
    }
  });
};

export const WorkspaceContentSchema = WorkspaceContentShape.superRefine(issues);

/**
 * `shared` plus the test-only `overlay`, add-only: the overlay may add places and items, never replace a key, an id, a
 * fixture ref or a place's scalar settings. Fails fast on any collision.
 */
export function mergeWorkspaceContent(shared: WorkspaceContent, overlay: WorkspaceContent): WorkspaceContent {
  const fail = (what: string) => {
    throw new Error(`content overlay replaces ${what}`);
  };
  const sharedKeys = itemKeys(shared);
  for (const key of itemKeys(overlay).keys()) if (sharedKeys.has(key)) fail(`item "${key}"`);
  for (const key of Object.keys(overlay.ids)) if (key in shared.ids) fail(`ids.${key}`);
  for (const name of Object.keys(overlay.fixtureRefs)) if (name in shared.fixtureRefs) fail(`fixtureRefs.${name}`);
  for (const field of ["messageContent", "promptContent", "externalIdSeparator"] as const) {
    if (JSON.stringify(overlay[field]) !== JSON.stringify(shared[field])) fail(field);
  }
  const places = shared.places.map((place) => {
    const extra = overlay.places.find((p) => p.workspace === place.workspace);
    if (extra === undefined) return place;
    if (extra.publicWorkspace) fail(`places.${place.workspace}.publicWorkspace`);
    if (extra.demoUsers !== null) fail(`places.${place.workspace}.demoUsers`);

    const merged = { ...place, connectors: { ...place.connectors, ...extra.connectors } };
    for (const list of ITEM_LISTS) (merged as Record<string, unknown>)[list] = [...place[list], ...extra[list]];
    return merged;
  });
  const added = overlay.places.filter((p) => !shared.places.some((s) => s.workspace === p.workspace));
  return WorkspaceContentSchema.parse({
    ...shared,
    ids: { ...shared.ids, ...overlay.ids },
    fixtureRefs: { ...shared.fixtureRefs, ...overlay.fixtureRefs },
    places: [...places, ...added],
  });
}

export type WorkspaceContent = z.infer<typeof WorkspaceContentSchema>;
export type PlaceContentType = z.infer<typeof PlaceContent>;
export type ContentItemList = (typeof ITEM_LISTS)[number];
export { ITEM_LISTS as CONTENT_ITEM_LISTS };
